import React, { useEffect, useState, useCallback } from 'react';
import { getManageOrders, getOrders, patchOrder } from '../../services/orders';
import { toast } from 'react-toastify';
import { BiCopy } from 'react-icons/bi';
import Breadcrumbs from '../../components/BreadCrumbs';
import CustomLoading from '../../components/customLoading/CustomLoading';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import ShowModal from '../../utils/ShowModal';
import OrderOrdersDetailsPopView from './OrderOrdersDetailsPopView';

// Added RETURNED to status columns
const statusColumns = ["PENDING", "CONFIRMED", "IN_PACKAGING", "DISPATCHED", "DELIVERED", "CANCELLED", "RETURNED"];
const statusLabels = {
    PENDING: "Pending",
    CONFIRMED: "Confirmed",
    IN_PACKAGING: "Packaging",
    DISPATCHED: "Dispatched",
    DELIVERED: "Delivered",
    CANCELLED: "Cancelled",
    RETURNED: "Returned"
};

const ItemTypes = {
    ORDER: 'order'
};

const columnColorMap = {
    PENDING: 'bg-yellow-50 border-yellow-200',
    CONFIRMED: 'bg-orange-50 border-orange-200',
    IN_PACKAGING: 'bg-blue-50 border-blue-200',
    DISPATCHED: 'bg-indigo-50 border-indigo-200',
    DELIVERED: 'bg-green-50 border-green-200',
    CANCELLED: 'bg-red-50 border-red-200',
    RETURNED: 'bg-purple-50 border-purple-200',
};

const headerColorMap = {
    PENDING: 'bg-yellow-100 text-yellow-800',
    CONFIRMED: 'bg-orange-100 text-orange-800',
    IN_PACKAGING: 'bg-blue-100 text-blue-800',
    DISPATCHED: 'bg-indigo-100 text-indigo-800',
    DELIVERED: 'bg-green-100 text-green-800',
    CANCELLED: 'bg-red-100 text-red-800',
    RETURNED: 'bg-purple-100 text-purple-800',
};

const OrderCard = ({ item, status, moveOrder, onClick }) => {
    const [{ isDragging }, drag] = useDrag({
        type: ItemTypes.ORDER,
        item: { id: item.order_id, status },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    const copyClipboard = (text) => {
        navigator.clipboard.writeText(text)
            .then(() => toast.success('Copied to clipboard!'))
            .catch(() => toast.error('Failed to copy address'));
    };

    const handleClick = (e) => {
        if (!isDragging) {
            e.stopPropagation();
            onClick(item);
        }
    };

    return (
        <div
            ref={drag}
            className={`mb-3 cursor-grab ${isDragging ? 'opacity-50 scale-95' : 'opacity-100 scale-100'} transition-all duration-200`}
            onClick={handleClick}
        >
            <div
                className={`relative block p-4 rounded-xl border-2 shadow-sm hover:shadow-lg transition-all duration-300 ${columnColorMap[status]} hover:translate-y-[-3px] hover:scale-[1.02] group`}
            >
                {/* Customer Info */}
                <div className="mb-3 pt-6">
                    {(item.order_type === "THIRD_PARTY" || item.third_party_order_id) && item.third_party_order_id ? (
                        <p
                            className="text-xs font-semibold text-indigo-700 truncate mb-2"
                            title={item.third_party_order_id}
                        >
                            {item.third_party_order_id}
                        </p>
                    ) : null}
                    <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-full flex items-center justify-center">
                            <span className="text-white text-xs font-bold">
                                {item.name ? item.name.charAt(0).toUpperCase() : '?'}
                            </span>
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-800 truncate" title={item.name}>
                                {item.name || 'Unknown Customer'}
                            </p>
                        </div>
                    </div>

                    {/* Phone Number */}
                    <div className="flex items-center gap-2 mb-2">
                        <svg className="w-4 h-4 text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        <span className="text-xs text-gray-600 flex-1 truncate">
                            {item.phone_number}
                        </span>
                        <button
                            className="p-1.5 text-indigo-500 hover:bg-indigo-100 rounded-md transition-colors group-hover:opacity-100 opacity-70"
                            onClick={(e) => {
                                e.stopPropagation();
                                copyClipboard(item.phone_number);
                            }}
                            title="Copy phone number"
                        >
                            <BiCopy size={14} />
                        </button>
                    </div>

                    {/* Platform */}
                    <div className="flex items-center gap-2 mb-3">
                        <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        <span className="text-xs text-gray-600 font-medium">
                            {item.platform || 'Unknown Platform'}
                        </span>
                    </div>
                </div>

                {/* Payment Status */}
                <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-600 font-medium">Payment:</span>
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${item.payment_status === 'COMPLETED'
                        ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                        : 'bg-red-100 text-red-800 border border-red-200'
                        }`}>
                        <span className={`w-2 h-2 rounded-full mr-1.5 ${item.payment_status === 'COMPLETED' ? 'bg-emerald-500' : 'bg-red-500'
                            }`}></span>
                        {item.payment_status === 'COMPLETED' ? 'Paid' : 'Pending'}
                    </span>
                </div>

                {/* Drag Indicator */}
                <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-50 transition-opacity duration-200">
                    <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M11 18c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2 2 .9 2 2zm-2-8c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0-6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm6 4c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
                    </svg>
                </div>
            </div>
        </div>
    );
};

const StatusColumn = ({ status, orders, moveOrder, onOrderClick }) => {
    const [{ isOver }, drop] = useDrop({
        accept: ItemTypes.ORDER,
        drop: (item) => {
            if (item.status !== status) {
                moveOrder(item.id, status);
            }
        },
        collect: (monitor) => ({
            isOver: monitor.isOver(),
        }),
    });

    // Status icons mapping
    const statusIcons = {
        PENDING: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
        ),
        CONFIRMED: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
        ),
        IN_PACKAGING: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
        ),
        DISPATCHED: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
        ),
        DELIVERED: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
        ),
        CANCELLED: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
        ),
        RETURNED: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
            </svg>
        )
    };

    return (
        <div
            ref={drop}
            className={`h-full rounded-xl border-2 shadow-sm transition-all duration-300 ${columnColorMap[status]} ${isOver ? 'ring-4 ring-indigo-400/30 shadow-lg scale-[1.02] border-indigo-300' : 'hover:shadow-md'
                }`}
        >
            {/* Enhanced Column Header */}
            <div className={`p-4 rounded-t-xl ${headerColorMap[status]} border-b-2 border-white/20`}>
                <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-white/20 rounded-lg">
                            {statusIcons[status]}
                        </div>
                        <h3 className="font-bold text-sm uppercase tracking-wider">
                            {statusLabels[status]}
                        </h3>
                    </div>
                    <div className="flex items-center gap-1">
                        <span className="bg-white/90 backdrop-blur-sm px-2.5 py-1 rounded-full text-xs font-bold shadow-sm border border-white/40">
                            {orders.length}
                        </span>
                    </div>
                </div>

                {/* Progress indicator */}
                <div className="w-full bg-white/30 rounded-full h-1">
                    <div
                        className="bg-white/70 h-1 rounded-full transition-all duration-500"
                        style={{
                            width: `${Math.min((orders.length / 10) * 100, 100)}%`
                        }}
                    ></div>
                </div>
            </div>

            {/* Drop Zone Indicator */}
            {isOver && (
                <div className="absolute inset-0 bg-indigo-100/50 backdrop-blur-sm rounded-xl border-2 border-dashed border-indigo-400 flex items-center justify-center z-10">
                    <div className="bg-white/90 backdrop-blur-sm rounded-lg p-4 shadow-lg border border-indigo-200">
                        <div className="flex items-center gap-2 text-indigo-600">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                            </svg>
                            <span className="font-semibold text-sm">Drop to move to {statusLabels[status]}</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Orders Container */}
            <div className="p-3 overflow-y-auto relative" style={{ maxHeight: 'calc(100vh - 280px)' }}>
                {orders.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                            <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 009.586 13H7" />
                            </svg>
                        </div>
                        <p className="text-sm text-gray-500 font-medium">No orders</p>
                        <p className="text-xs text-gray-400 mt-1">Drag orders here to update status</p>
                    </div>
                ) : (
                    orders.map((item) => (
                        <OrderCard
                            key={item.order_id}
                            item={item}
                            status={status}
                            moveOrder={moveOrder}
                            onClick={onOrderClick}
                        />
                    ))
                )}
            </div>
        </div>
    );
};

function ManageOrdersView() {
    const [loading, setLoading] = useState(false);
    const [ordersData, setOrdersData] = useState({});
    const [modalIsOrderDetailsOpen, setIsOrderDetailsOpen] = useState(false);
    const [openingItem, setOpeningItem] = useState({});

    const openDetailsModal = (item) => {
        setOpeningItem(item);
        setIsOrderDetailsOpen(true);
    };

    const closeDetailsModal = () => {
        setIsOrderDetailsOpen(false);
        setOpeningItem({});
    };

    const fetchData = useCallback(async (query = '') => {
        setLoading(true);
        try {
            let res = await getManageOrders(query);
            if (res.status === 200) {
                // Initialize all status columns with empty arrays if not present
                const initializedData = statusColumns.reduce((acc, status) => {
                    acc[status] = res.data[status] || [];
                    return acc;
                }, {});
                setOrdersData(initializedData);
            } else {
                toast.error("Unable to fetch orders");
            }
        } catch (err) {
            toast.error("Error fetching data");
        }
        setLoading(false);
    }, []);

    const patchOrderStatus = async (orderId, newStatus) => {
        try {
            await patchOrder({ id: orderId, data: { status: newStatus } });
            toast.success(`Order status updated to ${statusLabels[newStatus]}`);
            fetchData();
        } catch (err) {
            toast.error("Status update failed");
            fetchData();
        }
    };

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const moveOrder = useCallback((orderId, newStatus) => {
        setOrdersData(prevData => {
            // Find the order in any status column
            let foundOrder = null;
            let sourceStatus = null;

            for (const status in prevData) {
                const orderIndex = prevData[status].findIndex(o => o.order_id === orderId);
                if (orderIndex !== -1) {
                    foundOrder = prevData[status][orderIndex];
                    sourceStatus = status;
                    break;
                }
            }

            if (!foundOrder) return prevData;

            // Remove from source status
            const updatedData = {
                ...prevData,
                [sourceStatus]: prevData[sourceStatus].filter(o => o.order_id !== orderId),
                [newStatus]: [...(prevData[newStatus] || []), foundOrder]
            };

            return updatedData;
        });

        patchOrderStatus(orderId, newStatus);
    }, []);

    return (
        <div className="">
            <Breadcrumbs items={[
                { name: 'Home', link: '/dashboard' },
                { name: 'Manage Orders' }
            ]} />

            {/* Enhanced Header Section */}
            <div className="mt-2 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl shadow-sm p-6 mb-6">
                <div className="flex items-center justify-between">
                    {/* Title Section */}
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-indigo-500 rounded-xl flex items-center justify-center shadow-md">
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                            </svg>
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-800">Order Management</h1>
                            <p className="text-sm text-gray-600 flex items-center gap-1.5 mt-1">
                                <svg className="w-4 h-4 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                                </svg>
                                Drag and drop orders between status columns to update their status
                            </p>
                        </div>
                    </div>

                    {/* Action Section */}
                    <div className="flex items-center gap-3">
                        {/* Order Statistics */}
                        <div className="hidden lg:flex items-center gap-4 bg-white/70 backdrop-blur-sm rounded-lg px-4 py-2 border border-indigo-100">
                            <div className="text-center">
                                <p className="text-xs text-gray-500">Total Orders</p>
                                <p className="text-lg font-semibold text-indigo-600">
                                    {Object.values(ordersData).reduce((acc, orders) => acc + orders.length, 0)}
                                </p>
                            </div>
                            <div className="w-px h-8 bg-indigo-200"></div>
                            <div className="text-center">
                                <p className="text-xs text-gray-500">Pending</p>
                                <p className="text-lg font-semibold text-yellow-600">
                                    {(ordersData.PENDING || []).length}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="flex flex-col justify-center items-center h-64 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl border border-indigo-200">
                    <CustomLoading size={80} />
                    <p className="text-indigo-600 font-medium mt-4">Loading orders...</p>
                </div>
            ) : (
                <DndProvider backend={HTML5Backend}>
                    <div className="space-y-4">
                        {/* Desktop View - Horizontal Kanban */}
                        <div className="hidden lg:block">
                            <div className="grid grid-cols-5 gap-4" style={{ minHeight: 'calc(100vh - 400px)' }}>
                                {statusColumns.map((status) => (
                                    <StatusColumn
                                        key={status}
                                        status={status}
                                        orders={ordersData[status] || []}
                                        moveOrder={moveOrder}
                                        onOrderClick={openDetailsModal}
                                    />
                                ))}
                            </div>
                        </div>

                        {/* Tablet View - 3 Columns */}
                        <div className="hidden md:block lg:hidden">
                            <div className="grid grid-cols-3 gap-4" style={{ minHeight: 'calc(100vh - 400px)' }}>
                                {statusColumns.slice(0, 3).map((status) => (
                                    <StatusColumn
                                        key={status}
                                        status={status}
                                        orders={ordersData[status] || []}
                                        moveOrder={moveOrder}
                                        onOrderClick={openDetailsModal}
                                    />
                                ))}
                            </div>
                            <div className="grid grid-cols-4 gap-4 mt-4" style={{ minHeight: 'calc(100vh - 600px)' }}>
                                {statusColumns.slice(3).map((status) => (
                                    <StatusColumn
                                        key={status}
                                        status={status}
                                        orders={ordersData[status] || []}
                                        moveOrder={moveOrder}
                                        onOrderClick={openDetailsModal}
                                    />
                                ))}
                            </div>
                        </div>

                        {/* Mobile View - Single Column with Tabs */}
                        <div className="block md:hidden">
                            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                                {/* Mobile Status Tabs */}
                                <div className="flex overflow-x-auto bg-gray-50 border-b">
                                    {statusColumns.map((status) => (
                                        <button
                                            key={status}
                                            className={`flex-shrink-0 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${status === 'PENDING'
                                                ? 'border-indigo-500 text-indigo-600 bg-white'
                                                : 'border-transparent text-gray-600 hover:text-gray-800'
                                                }`}
                                        >
                                            <div className="flex items-center gap-2">
                                                <span>{statusLabels[status]}</span>
                                                <span className="bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full text-xs">
                                                    {(ordersData[status] || []).length}
                                                </span>
                                            </div>
                                        </button>
                                    ))}
                                </div>

                                {/* Mobile Status Content */}
                                <div className="p-4">
                                    {statusColumns.map((status) => (
                                        <div key={status} className={status === 'PENDING' ? 'block' : 'hidden'}>
                                            <div className="space-y-3">
                                                {(ordersData[status] || []).length === 0 ? (
                                                    <div className="text-center py-8">
                                                        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                                            <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 009.586 13H7" />
                                                            </svg>
                                                        </div>
                                                        <p className="text-sm text-gray-500">No {statusLabels[status].toLowerCase()} orders</p>
                                                    </div>
                                                ) : (
                                                    (ordersData[status] || []).map((item) => (
                                                        <OrderCard
                                                            key={item.order_id}
                                                            item={item}
                                                            status={status}
                                                            moveOrder={moveOrder}
                                                            onClick={openDetailsModal}
                                                        />
                                                    ))
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </DndProvider>
            )}

            <ShowModal
                contentLabel={"Order Details alert"}
                isOpen={modalIsOrderDetailsOpen}
                modalWidth="50%"
                isSidebar={true}
                onRequestClose={closeDetailsModal}
                componentToPass={<OrderOrdersDetailsPopView order_id={openingItem?.order_id} />}
            />
        </div>
    );
}

export default ManageOrdersView;